// The mate's PROPOSAL CARD (`.context/alarms-redesign/spec-all.md`, "Mate
// proposal card — PHASE 3"), rendered directly under the mate bar.
//
// It is the consent step, and nothing else. The mate never auto-applies (ADR
// 0161): the card shows exactly what would be created — the server-built
// `display`, so the card can never disagree with what the validator accepted —
// and CONFIRM posts the proposal's opaque `body` VERBATIM through the ordinary
// owner route `POST /v1/boats/:boatId/alerts`, with the owner's own session and
// a fresh idempotency key. There is no model-issued write in this path.
//
// Same request, errors and wording as `components/NeoAlarmProposalCard.tsx` (the
// Neo chat panel's card) — this file is the Alarms page's CHROME for that same
// convention, not a second protocol. Deliberate differences, both in the
// wording:
//
//   - The design's footer caption ("Every slot is still hand-editable — same
//     editor as manual") would be a lie here: this card renders the proposal as
//     plain styled text, not as editable slots. It says what is true instead —
//     confirm, then edit it like any other alarm, in the row's own editor.
//   - Threshold lines are printed EXACTLY as the server built them
//     (`NeoAlarmProposalDisplay.thresholds`), which is what makes the card
//     unable to disagree with the body the validator accepted.
//
//     KNOWN GAP (ADR 0189). Those strings are in the BOAT's units, not the
//     account's — the server does no unit conversion, and it builds them from
//     the recipe metadata and the signal's declared unit. This card cannot fix
//     that: it holds pre-formatted text, not numbers with categories, so there
//     is nothing here to convert. The honest fix is server-side (build the
//     display against the requesting account's `unitSettings`) or a display
//     shape that carries the numbers; either is a contract change, not a
//     rendering one. Re-deriving the lines client-side is the ONE thing not to
//     do — it would let the card claim something other than what CONFIRM posts.

import { useState } from 'react';

import { api } from '../../../api';
import { severityBadgeTone } from '../../../lib/alarmDisplay';
import { mateArmedLine, mateTranscriptLine } from '../../../lib/mateBar';
import {
  proposalCreateErrorMessage,
  proposalSeverityLabel,
  proposalWatchLine,
  type AlarmCreateResponse,
  type NeoAlarmProposal,
} from '../../../lib/neoAlarmProposal';
import { ALARMS_UPDATED_EVENT } from '../../NeoAlarmProposalCard';
import { Icon } from '../../ui/Icon';

/**
 * The mate's mark: a bell with a plus, for adding an alarm (TOM-568). It was a
 * sparkle, which since TOM-547 means Neo's chat on every page. The bar imports
 * it from here, as it already imports this card.
 */
export function MateAlarmIcon() {
  return <Icon name="bell-plus" size={18} />;
}

interface MateProposalCardProps {
  /** The draft, exactly as the server reported it. */
  readonly proposal: NeoAlarmProposal;
  /** What the owner typed to get it — the card quotes it back. */
  readonly command: string;
  /** Called after a 201, with the created alarm's title and compile status, so
   * the bar can clear the drafts and show its receipt. */
  readonly onCreated: (title: string, compileStatus: string | null) => void;
  readonly onDiscard: () => void;
}

function pillClassFor(proposal: NeoAlarmProposal): string {
  switch (severityBadgeTone(proposal.display.severity)) {
    case 'danger':
      return 'al-pill--alarm';
    case 'attention':
      return 'al-pill--warning';
    default:
      return 'al-pill--info';
  }
}

export function MateProposalCard({
  proposal,
  command,
  onCreated,
  onDiscard,
}: MateProposalCardProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { display } = proposal;
  const armed = mateArmedLine(display.enabledModes);

  async function confirm(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      // The proposal's own stable id, NOT a fresh key per click: the route
      // dedupes on it, so a second press — or a retry after a lost response —
      // replays the cached 201 instead of creating a second alarm.
      const res = await api<AlarmCreateResponse>(`/v1/boats/${proposal.boatId}/alerts`, {
        method: 'POST',
        body: proposal.body,
        idempotencyKey: proposal.clientId,
      });
      // An open Alarms page (this one, and any other tab) refetches its snapshot
      // off this event — the same broadcast the chat panel's card fires.
      window.dispatchEvent(new CustomEvent(ALARMS_UPDATED_EVENT));
      onCreated(display.title, res.compileStatus);
    } catch (err) {
      setError(proposalCreateErrorMessage(err));
      setBusy(false);
    }
  }

  return (
    <article className="al-mate-card" data-testid="alarms-mate-proposal">
      <p className="al-mate-card__transcript">
        <span className="al-mate-avatar al-mate-avatar--sm" aria-hidden="true">
          <MateAlarmIcon />
        </span>
        <span>{mateTranscriptLine(command)}</span>
      </p>

      <p className="al-mate-card__head">
        <span className={`al-pill ${pillClassFor(proposal)}`}>
          {proposalSeverityLabel(display.severity)}
        </span>
        <strong className="al-mate-card__title">{display.title}</strong>
      </p>

      <p className="al-mate-card__watch">{proposalWatchLine(display)}</p>

      {display.thresholds.length > 0 ? (
        <ul className="al-mate-card__lines">
          {display.thresholds.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}

      {display.conditions !== undefined ? (
        <p className="al-mate-card__sub">Only while {display.conditions}</p>
      ) : null}
      {armed !== null ? <p className="al-mate-card__sub">{armed}</p> : null}
      {display.bindingPath !== undefined ? (
        <p className="al-mate-card__sub">Pinned to {display.bindingPath}</p>
      ) : null}

      {error !== null ? (
        <p className="al-mate-card__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="al-mate-card__actions">
        <button
          type="button"
          className="al-btn al-btn--primary"
          onClick={() => void confirm()}
          disabled={busy}
          data-testid="alarms-mate-confirm"
        >
          {busy ? 'Saving…' : error !== null ? 'Try again' : 'Add alarm'}
        </button>
        <button
          type="button"
          className="al-btn al-btn--secondary"
          onClick={onDiscard}
          disabled={busy}
          data-testid="alarms-mate-discard"
        >
          Discard
        </button>
        <span className="al-mate-card__caption">
          Adding saves it to the boat. You can edit it afterwards.
        </span>
      </div>
    </article>
  );
}
